import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import RecordUsage from 'components/record-shell/RecordUsage';
import { api } from 'services/api';
import history from 'services/history';
import { Specialty } from 'types/specialty';
import SpecialtyForm from '../form/SpecialtyForm';

const EditSpecialty: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [specialty, setSpecialty] = useState<Specialty | null>(null);
  const [savedName, setSavedName] = useState('');

  const loadSpecialty = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get<Specialty>(`/specialties/${id}`)
      .then(response => {
        setSpecialty(response.data);
        setSavedName(response.data.name);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados da especialidade');
        history.push('/specialties');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadSpecialty().finally(() => setLoading(false));
  }, [loadSpecialty]);

  function goBack() {
    history.push('/specialties');
  }

  function handleChange<K extends keyof Specialty>(index: K, value: Specialty[K]) {
    setSpecialty(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!specialty) return;

    setSaving(true);

    api
      .put(`/specialties/${id}`, { id: specialty.id, name: specialty.name })
      .then(() => loadSpecialty())
      .then(() => toast.success('Especialidade atualizada'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar a especialidade');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!specialty) return;

    setValidation('');
    if (!specialty.name) {
      setValidation('Nome é obrigatório');
      return;
    }
    handleSubmit();
  }

  // descarta o que foi digitado e recarrega o cadastro
  function handleCancel() {
    setValidation('');
    setLoading(true);
    loadSpecialty().finally(() => setLoading(false));
  }

  function handleDelete() {
    setSaving(true);

    api
      .delete(`/specialties/${id}`)
      .then(() => {
        toast.success('Especialidade excluída com sucesso!');
        history.push('/specialties');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível excluir a especialidade');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  const usage = specialty?.usage;
  const inUse = !!usage && usage.procedures + usage.budgets > 0;

  return (
    <>
      <Appbar title="Especialidades" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          title="Deseja excluir esta especialidade?"
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleDelete}
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Especialidade'} backAction={goBack} />

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !specialty ? (
              <NoData message="Não foi possível carregar os dados da especialidade" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <SpecialtyForm handleChange={handleChange} specialty={specialty} validation={validation} />

                {usage && (
                  <RecordUsage
                    title="Onde é usada"
                    items={[
                      { count: usage.procedures, singular: 'procedimento', plural: 'procedimentos' },
                      { count: usage.budgets, singular: 'orçamento', plural: 'orçamentos' },
                    ]}
                    blockedMessage="Esta especialidade está em uso e não pode ser excluída."
                    freeMessage="Nenhum cadastro usa esta especialidade, então ela pode ser excluída."
                  />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && specialty && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving || inUse} onClick={() => setConfirmDialogOpen(true)}>
              Excluir especialidade
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditSpecialty;
